import { esEntries, get } from 'ctm-util-functions';

// eslint-disable-next-line valid-jsdoc
/**
 A simple model that wraps around the pageSchema and provides convenience functions for interacting with the schema.
 */
class PageSchemaModel {

    /**
     * Instantiates the model with the supplied pageSchema
     * @param {Object} schema - JSON schema for a page
     */
    constructor(schema) {
        this.schema = schema;
        this._createIndexOfSchema();
    }

    _createIndexOfSchema() {
        this.schemaAsDictionary = {};
        this.schemaAsOrderedList = [];
        if (this.schema.pages != null) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < this.schema.pages.length; i++) {
                this.schemaAsDictionary[this.schema.pages[i].name] = true;
                this.schemaAsOrderedList[i] = this.schema.pages[i].name;
            }
        }
    }

    /**
     * Returns a map of all pages.  If a page no longer exists in the
     * schema in-memory, the value for that page name is false.
     * @returns {bool} Whether the page exists in-memory
     */
    getDictionaryOfSchema() {
        return this.schemaAsDictionary;
    }

    /**
     * Returns the settings from the schema that apply to all pages
     * @returns {Object} - The settings from the page schema
     */
    getSettings = () => {
        return this.schema.settings;
    };

    /**
     * Get the URL prefix for each page name e.g. /some-vertical/journey
     * @returns {String} The qualifier setting
     */
    getUrlQualifier = () => {
        return this.getSettings().qualifier;
    };

    /**
     * Get the asset path, taking verticals and versions into account
     * @returns {String} - The base assets path for this vertical and version
     */
    getBaseAssetsPath = () => {
        const { vertical, version = false } = this.getSettings();
        const _base = '/evt/assets';
        return version ? `${_base}/${vertical}/${version}` : `${_base}/${vertical}`;
    };

    /**
     * Returns the base page in the journey
     * @returns {String} The journey's base page name.
     */
    getJourneyBasePageName = () => {
        const pageName = this.getSettings().basePage;
        const validPageName = pageName && typeof pageName === 'string' && pageName.charAt(0) === '/';
        if (!validPageName) {
            throw Error(`basePage MUST be a relative path, but received ${pageName}`);
        }
        return pageName;
    };

    /**
     * Returns the journey base path.
     * @returns {String} The journey base path.
     */
    getJourneyBasePagePath = () => {
        const basePage = this.getJourneyBasePageName();
        return basePage ? `${basePage}/journey` : basePage;
    };

    /**
     * Get all page schemas from the page schema object
     * @returns {Object} - all page schemas
     */
    getAllSchemas = () => {
        return [...this.schema.pages, ...this.schema.redirects || []];
    };

    /**
     * Get all journey page names
     * @param {String} pageName - An optional page name, if supplied, then only the page names up to and including this
     * page will be returned
     * @returns {Array} - an array of page names
     */
    getAllJourneyPageNames = (pageName = null) => {
        const allPages = this.schema.pages.map((page) => page.name);
        if (!pageName) {
            return allPages;
        }
        const pageIndex = allPages.findIndex(((name) => name === pageName));
        return allPages.slice(0, pageIndex + 1);
    };

    /**
     * Return all the page schema objects up until one named 'results' from a starting page.
     * @param {String|Null} pageName Pass in a page name if mid-journey, or if you want all pages up to results, don't pass any args.
     * @returns {Array} An array of page objects
     */
    getNextPagesUpToResults = (pageName = null) => {
        const allPages = this.getAllJourneyPageNames();
        const pageIndex = allPages.findIndex((page) => page === pageName);
        const resultsIndex = allPages.findIndex((page) => page === 'results');
        return allPages.slice(pageIndex + 1, resultsIndex);
    };

    /**
     * Return the pathname for the page with name === pageName
     * @param  {String} pageName The name of the page to get the path for
     * @returns {String|null}     The path for the page at pageName
     */
    getPathForPageName = (pageName) => {
        let pages = [...this.getAllSchemas()];
        while (pages.length > 0) {
            const page = pages.shift();
            if (page.name === pageName) {
                return page.path;
            }
            if (page.pages !== undefined) {
                pages = pages.concat(page.pages);
            }
        }
        return null;
    };

    /**
     * Return the name of a page for the given pagePath
     * @param  {String} pagePath - The name of the page to get the path for
     * @returns {String|null}     - The name of the page.
     */
    getPageNameForPath = (pagePath) => {
        let pages = [...this.getAllSchemas()];
        while (pages.length > 0) {
            const page = pages.shift();
            if (page.path === pagePath) {
                return page.name;
            }
            if (page.pages !== undefined) {
                pages = pages.concat(page.pages);
            }
        }
        return null;
    };

    /**
     * Get the page schema for the supplied path
     * @param {String} path - The path to the page whose schema is required
     * @returns {Object} - The page schema that represents the supplied path
     * @throws {Error} Will throw an error if there is no schema for path
     */
    getPageSchema = (path) => {
        const pageSchema = this.getAllSchemas().find((schema) => schema.path === path);
        if (!pageSchema) {
            throw Error("couldn't find a matching schema for path:", path);
        }
        return pageSchema;
    };

    /**
     * From the pageName, get the api schema object
     * @param {String} pageName The pageName e.g. occupancy
     * @returns {Object} The api schema object
     */
    getApiSchemaByPageName = (pageName) => {
        const path = this.getPathForPageName(pageName);
        const apiSchema = this.getPageSchema(path).api;
        if (apiSchema === undefined) {
            // We can't have a NextStepButton on a page if that page doesn't define an API schema
            // We don't need to catch this, this should only be an issue when setting up new schemas (ie in dev)
            throw Error(`No API schema defined for page: ${path}`);
        }
        return apiSchema;
    };

    /**
     * Return the pageSchema for the page with name === pageName
     * @param  {String} pageName - The name of the page to get the pageSchema for
     * @returns {Object|null} - The page schema that matches the page name
     */
    getPageSchemaByName = (pageName) => {
        return this.getAllSchemas().find((pageSchema) => pageSchema.name === pageName) || null;
    };

    /**
     * Gets the next page schema for the supplied path, or null if there is no next page.
     * @param {String} path - the path of a page that needs progression to another page
     * @returns {Object} - The next page schema for the supplied path, or null if there is no progression
     * from the supplied path
     * @throws {Error} Will throw an error if there is no next page from path
     */
    getNextPageSchema = (path) => {
        for (const [index, page] of esEntries(this.schema.pages)) {
            if (path === page.path) {
                return this.schema.pages[index + 1];
            }
        }
        throw Error('there is no next page for the path:', path);
    };

    /**
     * Gets the next page schema for the supplied path, or null if there is no next page.
     * @param {String} path - the path of a page that needs progression to another page
     * @returns {Object} - The next page schema for the supplied path, or null if there is no progression
     * from the supplied path
     * @throws {Error} Will throw an error if there is no next page from path
     */
    getPreviousPageSchema = (path) => {
        for (const [index, page] of esEntries(this.schema.pages)) {
            if (path === page.path) {
                if (index === 0) {
                    return null;
                }
                return this.schema.pages[index - 1];
            }
        }
        throw Error('there is no previous page for the path:', path);
    };

    /**
     * Return the first pageSchema, previous to path that requires validation
     * @param {String} path - the path of a page that needs progression to another page
     * @param {Object} formSchemaModel - The model for the form schema
     * @returns {Object} - The first pageSchema, previous to path that requires validation, or null if there are none
     *                     eg, the start page.
     * @todo: THIS IS NO LONGER USED - CHECK
     */
    getPreviousPageSchemaWithValidation = (path, formSchemaModel) => {
        let previousSchema = this.getPreviousPageSchema(path);
        if (previousSchema === null) {
            // looking for a previous schema for the start page
            return null;
        }
        // Not sure why this is needed
        while (!formSchemaModel.pageHasValidation(previousSchema.name)) {
            previousSchema = this.getPreviousPageSchema(previousSchema.path);
            if (previousSchema === null) {
                // previousSchema is for the start page
                return null;
            }
        }
        return previousSchema;
    };

    /**
     * Check if the Next page is a previous page in the schema.
     * @param  {String} currentPageName - The current page name
     * @param  {String} nextPagePath - The next page path
     * @returns {Boolean} True if it is a previous page in the schema.
     */
    isPreviousPageInSchema = (currentPageName, nextPagePath) => {
        const [currentPageIndex, nextPageIndex] = this.getCurrentAndNextPagesIndex(currentPageName, nextPagePath);
        return currentPageIndex > nextPageIndex;
    };

    /**
     * Check if the Next page is a later page in the schema.
     * @param  {String} currentPageName - Then current page name
     * @param  {String} nextPagePath - Then next page path
     * @returns {Boolean} True if it is a later page in the schema, false otherwise.
     */
    isLaterPageInSchema = (currentPageName, nextPagePath) => {
        const [currentPageIndex, nextPageIndex] = this.getCurrentAndNextPagesIndex(currentPageName, nextPagePath);
        return currentPageIndex < nextPageIndex;
    };

    /**
     * Check if the "page" matches the "nameOrPath".
     * @param {Object} page - A page object.
     * @param {String} nameOrPath - Either the "name" or "path" of the page to check.
     * @returns {Boolean} True the page matches nameOrPath, false otherwise.
     */
    pageMatchesNameOrPath = (page, nameOrPath) => {
        const nameMatch = page.name === nameOrPath;
        const pathMatch = page.path === nameOrPath;
        return nameMatch || pathMatch;
    };

    /**
     * Check if a page matches the first step of the journey.
     * @param {String} nameOrPath - Either the "name" or "path" of the page to check.
     * @returns {Boolean} True if the page is the first step of the journey, otherwise false.
     */
    isFirstStepInJourney = (nameOrPath) => {
        const firstStep = this.getFirstStepPage();
        return this.pageMatchesNameOrPath(firstStep, nameOrPath.split('?')[0]);
    };

    /**
     * Get the Current and the next page indexes.
     * @param  {String} currentPageName - Then current page name
     * @param  {String} nextPagePath - Then next page path
     * @returns {Array} - Current and next page indexes.
     */
    getCurrentAndNextPagesIndex = (currentPageName, nextPagePath) => {
        const currentPageIndex = this.schema.pages.findIndex((page) => {
            return page.name === currentPageName;
        });

        const nextPageIndex = this.schema.pages.findIndex((page) => {
            return page.path === nextPagePath;
        });

        return [currentPageIndex, nextPageIndex];
    };

    /**
     * Gets the URL for the next page in the journey.
     * @param {Object} page - A page object.
     * @param {String} page.path - The path of the page to check.
     * @returns {String} The URL of the next page in the progression
     */
    getNextPageUrl = ({ path }) => this.getNextPageSchema(path).path;

    /**
     * Get all pages that form the journey.
     * @returns {Object[]} List of all pages in the journey.
     */
    getAllPages = () => this.schema.pages;

    /**
     * Get all pages that form the journey.
     * @param {string} pageName - name of the requested page
     * @returns {Object} return the required page object.
     */
    getPageByName = (pageName) => {
        return this.schema.pages.find((page) => page.name === pageName);
    };

    /**
     * Removes a page model from the schema.
     * @param {string} pageName - name of the requested page
     */
    removePageByName = (pageName) => {
        for (let i = 0; i < this.schema.pages.length; i += 1) {
            if (this.schema.pages[i].name === pageName) {
                this.schema.pages.splice(i, 1);
                this.schemaAsDictionary[pageName] = false;
                return;
            }
        }
    };

    /**
     * Get all pages for each step in the journey.
     * @returns {Object[]} List of all pages in the journey.
     */
    getAllStepPages = () => this.getAllPages().filter((page) => page.inProgressBar);

    /**
     * Get the page for the first step in the journey.
     * @returns {Object} The first step page.
     */
    getFirstStepPage = () => this.getAllStepPages()[0] || {};

    /**
     * Get the page for the next step in the journey, based on the passed in page.
     * @param {String} nameOrPath - Either the "name" or "path" of the page to check.
     * @returns {Object} The next page in the journey.
     */
    getNextStepPage = (nameOrPath) => {
        const pages = this.getAllStepPages();
        const currentPageIndex = pages.findIndex((page) => this.pageMatchesNameOrPath(page, nameOrPath));
        if (currentPageIndex !== -1) {
            const nextJourneyPage = pages[currentPageIndex + 1];
            if (nextJourneyPage) {
                return nextJourneyPage;
            }
        }
        return {};
    };

    /**
     * Get all the previous pages in the journey that are part of the main steps.
     * @param {String} nameOrPath - Either the "name" or "path" of the page to check.
     * @returns {Object[]} An array of page schemas.
     */
    getAllPreviousStepPages = (nameOrPath) => {
        const allPreviousStepPages = [];
        const stepPageExists = !!this.getAllStepPages()
            .filter((page) => this.pageMatchesNameOrPath(page, nameOrPath))
            .shift();
        if (stepPageExists) {
            const validPreviousPage = (page) => {
                const validPage = get(page, 'name') && get(page, 'path');
                const isCurrentPage = this.pageMatchesNameOrPath(page, nameOrPath);
                return validPage && !isCurrentPage;
            };
            let stepPage = this.getFirstStepPage();
            while (validPreviousPage(stepPage)) {
                allPreviousStepPages.push(stepPage);
                stepPage = this.getNextStepPage(stepPage.path);
            }
        }
        return allPreviousStepPages;
    };

    /**
     * Get the redirectTo path of the current page.
     *
     * This property is used to prevent users requesting certain pages directly (e.g. /moreInfo)
     * as this could leave them the user in a bad state (e.g. what happens when they click back)!
     *
     * Instead of dealing with all these edge cases separately and in code, we have moved this
     * into the schema and calling it "business logic". This basically makes it easier to deal with.
     *
     * @param {Object} page - A page object.
     * @param {String} page.path - The path of the page to check.
     * @returns {string|undefined} The redirectTo path of the current page.
     */
    getLandingPageRedirect = ({ path }) => {
        const pages = this.getAllPages();
        const currentPage = pages.filter((page) => page.path === path).shift();
        return get(currentPage, 'whenLandingPage.redirectTo');
    };

    /**
     * Gets the last page of the Journey
     * @param {String} basePath - the base path of the journey defined in page schema
     * @returns {Object} - an object with some page details.
     */
    getPageJourneyLastPage = (basePath = `${this.getJourneyBasePageName()}/journey`) => {
        return this.schema.pages.slice(-1)[0];
    };

    /**
     * Constructs an array of page details for every page to include in the progress bar
     * @param {String} pageCategory - used when an app has multiple different progressBars, if supplied, just get the pages that match this pageKey
     * @returns {Array} - an array of page details
     */
    getPageJourneyForProgressBar = (pageCategory = null) => {
        return this.schema.pages.reduce((acc, page) => {
            if (page.inProgressBar) {
                if (pageCategory && pageCategory !== page.pageCategory) {
                    return acc;
                }
                acc.push(page);
            }
            return acc;
        }, []);
    };

    /**
     * Get snippets keys for a particular page by concat default snippets in pageSetting with page specific snippets
     * @param {String} path - The path to the page whose schema is required
     * @returns {Array} - an array of snippet keys
     */
    getSnippetsForPage = (path) => {
        const defaultSnippets = this.getSettings().snippets || [];
        const pageSnippets = this.getPageSchema(path).snippets || [];
        return [...defaultSnippets, ...pageSnippets];
    };

    /**
     * Take an array of journey pages, sequence the ones with "onMount" action together using the "onNext" property.
     * @param {Object[]} journeyPages - All the journey pages to sequence.
     * @returns {Object} A sequence of all journey pages.
     */
    sequenceApiOnMountSchemas = (journeyPages) => {
        const seenURLs = [];
        return (
            journeyPages
                // Shallow copy so we don't mutate.
                .slice()

                // Start with the previous page and then work back to the first page, so we can link them together.
                .reverse()

                // Convert the array into a single schema (essentially a linked list)
                .reduce((previousPages, { path }) => {
                    const pageSchema = this.getPageSchema(path);
                    const apiSchema = get(pageSchema, 'api.onMount', {});
                    if (seenURLs.includes(apiSchema.url)) {
                        // only add each url once
                        return {};
                    }
                    seenURLs.push(apiSchema.url);

                    // Simply return the apiSchema if it's the first page found (a.k.a last previous page due to reverse order).
                    if (Object.keys(previousPages).length === 0) {
                        return apiSchema;
                    }

                    // If the this page has an apiSchema, then link the previous schemas to it.
                    if (Object.keys(apiSchema).length === 0) {
                        return previousPages;
                    }
                    return { ...apiSchema, onNext: previousPages };
                }, {})
        );
    };

    /**
     * Retrieve [{title:"energy",routes:['/energy']}] for instance.
     * @param {Object} schema The complete page schema
     * @returns {Array} Array of objects per app, containing title and array of route strings.
     */
    getRoutesAsFlatArray = (schema) => {
        let routes = [];

        for (const routeSchema of schema) {
            if (routeSchema.pages) {
                routes = routes.concat(this.getRoutesAsFlatArray(routeSchema.pages));
            }
            routes = routes.concat(routeSchema.path);
        }
        return routes;
    };

    /**
     * Check if a page matches the prefill step of the journey.
     * @param {String} nameOrPath - Either the "name" or "path" of the page to check.
     * @returns {Boolean} True if the page is the prefill step of the journey, otherwise false.
     */

    isPrefillInJourney = (nameOrPath) => {
        const page = this.getAllPages().find((p) => this.pageMatchesNameOrPath(p, nameOrPath));
        return page && page.prefill;
    };

    isVerifyInJourney = (nameOrPath) => {
        const page = this.getAllPages().find((p) => this.pageMatchesNameOrPath(p, nameOrPath));
        return page && page.verify;
    };

    isQuotesExpiredInJourney = (nameOrPath) => {
        const page = this.getAllPages().find((p) => this.pageMatchesNameOrPath(p, nameOrPath));
        return page && page.quotes_expired;
    };

}

export { PageSchemaModel };
